package com.dotdrives.web.pages.drives

import com.dotdrives.controller.DriveDetailController
import com.dotdrives.controller.DriveDetailTab
import com.dotdrives.controller.DriveDetailUiState
import com.dotdrives.design.DotTone
import com.dotdrives.model.DrivePhase
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import kotlinx.browser.document
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface DriveDetailPanelProps : Props {
    var giftController: com.dotdrives.controller.WorkflowController
    var giftState: com.dotdrives.controller.WorkflowState
    var modalBlocked: Boolean
    var onReminder: () -> Unit
    var onRecordGift: () -> Unit
    var moveController: com.dotdrives.controller.WorkflowController?
    var moveState: com.dotdrives.controller.WorkflowState?
    var state: DriveDetailUiState
    var controller: DriveDetailController
    var phases: List<DrivePhase>
    var canWrite: Boolean
    var onClose: () -> Unit
    var onMoveToPhase: (DrivePhase) -> Unit
}

val DriveDetailPanel = FC<DriveDetailPanelProps> { props ->
    com.dotdrives.web.ui.useDialogFocus(true, "drive-detail-dialog", active = !props.modalBlocked, onClose = props.onClose)
    val drive = props.state.drive

    div {
        className = ClassName("drives-detail-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onClose() }

        div {
            className = ClassName("drives-detail-panel")
            id = web.dom.ElementId("drive-detail-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Drive details"
            onClick = { it.stopPropagation() }

            if (props.moveController != null && props.moveState != null) div {
                className = ClassName("drives-detail-head")
                com.dotdrives.web.ui.WorkflowFeedback { controller = props.moveController!!; state = props.moveState!! }
            }
            if (props.giftState.pending != null || props.giftState.error != null) div {
                className = ClassName("drives-detail-head")
                com.dotdrives.web.ui.WorkflowFeedback { controller = props.giftController; state = props.giftState }
            }
            if (props.state.loading && drive == null) {
                div {
                    className = ClassName("drives-detail-loading")
                    repeat(6) { DotSkeleton { height = if (it == 0) "54px" else "42px" } }
                }
            } else if (drive == null) {
                div {
                    className = ClassName("drives-detail-loading")
                    props.state.error?.let { error ->
                        DotBanner {
                            message = error
                            onRetry = props.controller::retry
                        }
                    }
                }
            } else {
                val currentPhase = props.phases.firstOrNull { it.id == drive.currentPhaseId }
                val nextPhase = currentPhase?.let { phase ->
                    props.phases.firstOrNull { it.sequence > phase.sequence }
                }

                div {
                    className = ClassName("drives-detail-head")
                    div {
                        className = ClassName("drives-detail-title-row")
                        div {
                            div { className = ClassName("drives-detail-title"); +drive.title }
                            div {
                                className = ClassName("drives-detail-subtitle")
                                +listOfNotNull(drive.giverDisplayName, drive.target)
                                    .joinToString(" · ")
                            }
                        }
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("drives-detail-close")
                            ariaLabel = "Close drive details"
                            onClick = { props.onClose() }
                            +"✕"
                        }
                    }
                    div {
                        className = ClassName("drives-detail-pills-row")
                        DotPill {
                            text = (currentPhase?.name ?: "").uppercase()
                            tone = DotTone.Primary
                        }
                        drive.tags.forEach { tag ->
                            DotPill {
                                key = tag.unsafeCast<Key>()
                                text = tag
                                tone = DotTone.Neutral
                            }
                        }
                        if (props.canWrite && nextPhase != null) {
                            button {
                                type = ButtonType.buttonType
                                className = ClassName("drives-detail-move-button")
                                onClick = { props.onMoveToPhase(nextPhase) }
                                +"Move to ${nextPhase.name} →"
                            }
                        }
                    }
                    div {
                        className = ClassName("drives-detail-stats-grid")
                        listOf(
                            "PROPOSED" to props.state.financials?.proposedAmount,
                            "PLEDGED" to props.state.financials?.pledgedAmount,
                            "RECEIVED" to props.state.financials?.receivedAmount,
                        ).forEach { (label, amount) ->
                            div {
                                className = ClassName("drives-detail-stat")
                                div { className = ClassName("drives-detail-stat-label"); +label }
                                div { className = ClassName("drives-detail-stat-value"); +formatMoneyFull(amount) }
                            }
                        }
                    }
                }

                div {
                    className = ClassName("drives-detail-tabs")
                    listOf(
                        DriveDetailTab.Overview to "Overview",
                        DriveDetailTab.Tasks to "Tasks",
                        DriveDetailTab.Notes to "Notes",
                        DriveDetailTab.Files to "Files",
                        DriveDetailTab.Activity to "Activity",
                    ).forEach { (value, label) ->
                        button {
                            type = ButtonType.buttonType
                            className = ClassName(
                                "drives-detail-tab" + if (props.state.tab == value) " drives-detail-tab-active" else "",
                            )
                            onClick = { props.controller.setTab(value) }
                            +label
                        }
                    }
                }

                div {
                    className = ClassName("drives-detail-body")
                    props.state.error?.let { error ->
                        DotBanner {
                            message = error
                            onRetry = props.controller::retry
                            className = "drives-inline-banner"
                        }
                    }
                    when (props.state.tab) {
                        DriveDetailTab.Overview -> {
                            TaskSection(props.state, props.controller, currentPhase?.name, props.canWrite)
                            DetailsSection(drive, props.state)
                            FilesSection(props.state)
                        }
                        DriveDetailTab.Tasks -> TaskSection(props.state, props.controller, currentPhase?.name, props.canWrite)
                        DriveDetailTab.Notes -> NotesSection(props.state, props.controller, props.canWrite)
                        DriveDetailTab.Files -> FilesSection(props.state)
                        DriveDetailTab.Activity -> ActivitySection(props.state)
                    }
                }

                div {
                    className = ClassName("drives-detail-footer")
                    if (props.canWrite) button {
                        type = ButtonType.buttonType
                        className = ClassName("drives-footer-pill")
                        onClick = { props.onReminder() }
                        +"🔔 Reminder"
                    }
                    if (props.canWrite) {
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("drives-footer-pill")
                            onClick = {
                                props.controller.setTab(DriveDetailTab.Notes)
                                document.getElementById("drive-note-body")?.asDynamic()?.focus()
                            }
                            +"📝 Note"
                        }
                    }
                    if (props.canWrite) button {
                        type = ButtonType.buttonType
                        className = ClassName("drives-footer-pill")
                        onClick = { props.onRecordGift() }
                        +"💚 Record gift"
                    }
                }
            }
        }
    }
}

private fun react.ChildrenBuilder.TaskSection(
    state: DriveDetailUiState,
    controller: DriveDetailController,
    phaseName: String?,
    canWrite: Boolean,
) {
    val complete = state.tasks.count { it.isComplete }
    div {
        className = ClassName("drives-section-label")
        +"${(phaseName ?: "PHASE").uppercase()} PHASE TASKS · $complete OF ${state.tasks.size}"
    }
    if (state.tasks.isEmpty()) {
        div { className = ClassName("drives-empty-copy"); +"No checklist items for this phase." }
    } else {
        div {
            className = ClassName("drives-task-list")
            state.tasks.forEach { task ->
                button {
                    key = task.id.unsafeCast<Key>()
                    type = ButtonType.buttonType
                    className = ClassName(
                        "drives-task-item" + if (task.isComplete) " drives-task-item-done" else "",
                    )
                    disabled = !canWrite
                    onClick = { controller.toggleTask(task.id, !task.isComplete) }
                    div {
                        className = ClassName(
                            "drives-task-check" + if (task.isComplete) " drives-task-check-done" else "",
                        )
                        if (task.isComplete) +"✓"
                    }
                    div {
                        className = ClassName("drives-task-copy")
                        div {
                            className = ClassName(
                                "drives-task-name" + if (task.isComplete) " drives-task-name-done" else "",
                            )
                            +task.name
                        }
                        task.completedAt?.let { completedAt ->
                            div {
                                className = ClassName("drives-task-meta")
                                +"Completed ${completedAt.toString().substringBefore('T')}"
                            }
                        }
                    }
                    span { className = ClassName("drives-task-category"); +task.category.uppercase() }
                }
            }
        }
    }
}

private fun react.ChildrenBuilder.DetailsSection(drive: com.dotdrives.model.Drive, state: DriveDetailUiState) {
    val lastActivityAt = state.activity.maxByOrNull { it.occurredAt }?.occurredAt
    div { className = ClassName("drives-section-label"); +"DETAILS" }
    div {
        className = ClassName("drives-details-list")
        listOfNotNull(
            "Steward" to (drive.stewardName ?: "Unassigned"),
            "Advocate" to drive.advocateGiverName,
            "Donation target" to drive.target,
            "Created" to drive.createdAt.toString().substringBefore('T'),
            "Last activity" to lastActivityAt?.toString()?.substringBefore('T'),
        ).forEach { (key, value) ->
            div {
                className = ClassName("drives-detail-row")
                span { className = ClassName("drives-detail-key"); +key }
                span { className = ClassName("drives-detail-value"); +(value ?: "—") }
            }
        }
    }
}

private fun react.ChildrenBuilder.FilesSection(state: DriveDetailUiState) {
    div { className = ClassName("drives-section-label"); +"FILES · ${state.files.size}" }
    if (state.files.isEmpty()) {
        div { className = ClassName("drives-empty-copy"); +"No files attached yet." }
    } else {
        div {
            className = ClassName("drives-file-list")
            state.files.forEach { file ->
                div {
                    key = file.id.unsafeCast<Key>()
                    className = ClassName("drives-file-item")
                    span { +"📄" }
                    span { className = ClassName("drives-file-name"); +file.fileName }
                    span { className = ClassName("drives-file-size"); +formatFileSize(file.fileSize) }
                }
            }
        }
    }
}

private fun react.ChildrenBuilder.ActivitySection(state: DriveDetailUiState) {
    if (state.activity.isEmpty()) {
        div { className = ClassName("drives-empty-copy"); +"No activity yet." }
    } else {
        div {
            className = ClassName("drives-task-list")
            state.activity.forEach { event ->
                div {
                    key = event.id.unsafeCast<Key>()
                    className = ClassName("drives-task-item")
                    div {
                        className = ClassName("drives-task-copy")
                        div {
                            className = ClassName("drives-task-name")
                            +event.verb.substringAfterLast('.').replace('_', ' ').replaceFirstChar(Char::uppercase)
                        }
                        div {
                            className = ClassName("drives-task-meta")
                            +event.occurredAt.toString().substringBefore('T')
                        }
                    }
                }
            }
        }
    }
}

private fun react.ChildrenBuilder.NotesSection(
    state: DriveDetailUiState,
    controller: DriveDetailController,
    canWrite: Boolean,
) {
    if (state.notes.isEmpty()) {
        div { className = ClassName("drives-empty-copy"); +"No notes yet." }
    } else {
        state.notes.forEach { note ->
            div {
                key = note.id.unsafeCast<Key>()
                className = ClassName("drives-task-item")
                div {
                    className = ClassName("drives-task-copy")
                    div { className = ClassName("drives-task-name"); +note.title.ifBlank { "Note" } }
                    div { className = ClassName("drives-task-meta"); +note.body }
                }
            }
        }
    }
    if (canWrite) {
        DriveNoteComposer {
            this.state = state
            this.controller = controller
        }
    }
}

private fun formatFileSize(bytes: Long): String = when {
    bytes >= 1_000_000 -> "${bytes / 1_000_000} MB"
    bytes >= 1_000 -> "${bytes / 1_000} KB"
    else -> "$bytes B"
}
